import { Button, Tag } from 'antd';
import type { DashboardLayoutConfigDto, DashboardTileId } from '@pams/shared';
import { DASHBOARD_TILE_LABEL } from '@/lib/labels';
import { EmptyState } from '@/components/EmptyState';
import { LAYOUT_COPY } from '../copy';
import { availableToAdd, isNewTile, unavailableTiles } from '../draft';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  §2 AND §5 — WHAT ELSE COULD GO ON THIS DASHBOARD, AND WHAT NEVER CAN. [P6b]
 *
 *  ⚠️ TWO LISTS, AND THAT IS HOW §2a AND §2c RECONCILE. §2a says an out-of-scope tile is
 *  "not greyed, not warned — absent from the picker"; §2c says the reason must be readable
 *  once, beside the unavailable tile. Those are two different lists, not a contradiction: the
 *  ADD control offers only what can be added, and a separate read-only list says why the rest
 *  cannot.
 *
 *  ⚠️ A DISABLED OPTION WOULD HAVE FAILED §2a. AntD still announces one, and `showSearch`
 *  still matches it — so typing "Load by division" on a police-station layout surfaces a row
 *  that does nothing, which is precisely "greyed and silent". And any explanation hung on it
 *  would live inside an overlay you must open, which is [D4.3]'s objection to hover
 *  affordances and a new census site besides.
 *
 *  ⚠️ THE SENTENCE COMES OFF THE WIRE. The server owns the wording of a refusal — the same
 *  rule that keeps `SCOPE_REASON` in `dashboardRegistry.service.ts` and English out of
 *  `packages/shared`. `web` must never re-derive it from `scopes`.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const TileLibrary = ({
  config,
  onLayoutIds,
  onAdd,
}: {
  config: DashboardLayoutConfigDto;
  onLayoutIds: ReadonlySet<DashboardTileId>;
  onAdd: (id: DashboardTileId) => void;
}) => {
  /*
   * ⚠️ [P6b.2] A REPEATABLE TILE STAYS IN THE PICKER WHILE IT IS ON THE LAYOUT. Every other
   * tile leaves it the moment it is placed — offering a second `SWAPS` is offering a no-op,
   * because `addEntry` refuses it. The asymmetry is read from the row's own `repeatable`,
   * which the server derives from the catalogue, so a second repeatable tile costs this
   * component nothing.
   */
  const addable = availableToAdd(config).filter((row) => row.repeatable || !onLayoutIds.has(row.id));
  const unavailable = unavailableTiles(config);
  const newCount = addable.filter((row) => isNewTile(config, row)).length;

  return (
    <div className="flex flex-col gap-6">
      <section aria-labelledby="tile-library-heading" className="flex flex-col gap-2">
        <h2 id="tile-library-heading" className="text-sm font-medium text-foreground">
          {addable.length === 0
            ? LAYOUT_COPY.library
            : /* §5 — "N tiles not on this layout", with the new ones counted. */
              `${String(addable.length)} ${LAYOUT_COPY.library.toLowerCase()}${
                newCount > 0 ? `, ${String(newCount)} new` : ''
              }`}
        </h2>

        {addable.length === 0 ? (
          <EmptyState size="sm" title={LAYOUT_COPY.libraryEmpty} />
        ) : (
          <ul data-testid="addable-tiles" className="flex flex-col gap-2">
            {addable.map((row) => (
              <li key={row.id} className="flex items-start gap-2 rounded-md border border-border p-2">
                <div className="min-w-0 flex-1">
                  <p className="flex items-center gap-2 text-sm font-medium text-foreground">
                    {DASHBOARD_TILE_LABEL[row.id].title}
                    {isNewTile(config, row) ? <Tag color="blue">{LAYOUT_COPY.newTile}</Tag> : null}
                  </p>
                  {/* §1c — read what it counts BEFORE switching it on. */}
                  <p className="text-xs text-muted-foreground">{DASHBOARD_TILE_LABEL[row.id].hint}</p>
                </div>
                {/* ⚠️ THE ACCESSIBLE NAME SAYS WHICH TILE — `ReorderableList`'s rule ("Move UB
                    Men up"). A list of buttons all called "Add" is one a screen reader user
                    cannot tell apart, and one a test can only reach by position. */}
                <Button
                  size="small"
                  aria-label={`${LAYOUT_COPY.add} ${DASHBOARD_TILE_LABEL[row.id].title}`}
                  onClick={() => {
                    onAdd(row.id);
                  }}
                >
                  {LAYOUT_COPY.add}
                </Button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {unavailable.length > 0 && (
        <section aria-labelledby="unavailable-tiles-heading" className="flex flex-col gap-2">
          <h2 id="unavailable-tiles-heading" className="text-sm font-medium text-foreground">
            {LAYOUT_COPY.unavailable}
          </h2>
          <p className="text-xs text-muted-foreground">{LAYOUT_COPY.unavailableHint}</p>
          <ul data-testid="unavailable-tiles" className="flex flex-col gap-2">
            {unavailable.map((row) => (
              <li key={row.id} className="rounded-md border border-dashed border-border p-2">
                <p className="text-sm text-muted-foreground">{DASHBOARD_TILE_LABEL[row.id].title}</p>
                {/* §2c — said once, at rest, in the operator's own words from the server. */}
                <p className="text-xs text-muted-foreground/80">{row.viewerReason ?? row.unavailableReason}</p>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
};
